// src/components/common/SearchForm/searchFormUtils.ts

import { getUserCoordinates, getCoordinatesFromTimezone } from '../../../utils/locationService';

export const getUserLocation = async (): Promise<{
  latitude: number;
  longitude: number;
  cityName: string;
}> => {
  try {
    // Use the location service which handles permission and fallback
    const coordinates = await getUserCoordinates();

    // Get city name from coordinates
    const cityName = await reverseGeocode(
      coordinates.latitude.toString(),
      coordinates.longitude.toString()
    );

    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
      cityName,
    };
  } catch {
    const fallback = getCoordinatesFromTimezone();
    return { latitude: fallback.latitude, longitude: fallback.longitude, cityName: "" };
  }
};

/**
 * Reverse geocodes coordinates to get city name
 */
export const reverseGeocode = async (lat: string, lon: string): Promise<string> => {
  try {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      throw new Error("Google Maps API key is missing.");
    }
    
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${apiKey}`
    );
    
    const data = await response.json();
    if (data.status !== "OK") {
      throw new Error(`Google Maps API error: ${data.status}`);
    }
    
    const components = data.results[0]?.address_components || [];
    const locality = components.find((c: unknown) => c.types.includes("locality"))?.long_name;
    const adminArea =
      components.find((c: unknown) => c.types.includes("administrative_area_level_1"))?.long_name ||
      components.find((c: unknown) => c.types.includes("administrative_area_level_2"))?.long_name;

    return locality || adminArea || "";
  } catch {
    return "";
  }
};

/**
 * Format a date for API requests
 */
export const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats airport display text
 */
export const formatAirportText = (airport: unknown): string => {
  if (!airport) return '';
  return `${airport.name}, ${airport.city}, ${airport.country_name}`;
};